import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Field, useForm } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from 'ncoded-component-library';
import TextAreaField from 'components/TextAreaField';
import ContactNumberField from 'components/ContactNumberField';
import EmailField from 'components/EmailField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import validations from 'validations';
import {
  CONTEXT_MAX_LENGTH,
  CONTEXT_ROWS,
} from 'components/UserEnquiryContextContainer/components/UserEnquiryForm/UserEnquiryForm.component';
import EnquiryContextOptions from './components/EnquiryContextOptions';
import { UserEnquiryContext } from 'models/UserEnquiryContext';

import './ConfirmDetails.styles.scss';
import './ConfirmDetails.styles.responsive.scss';

const CONTEXT_NAME = 'context';

type ConfirmDetailsProps = {
  invalid: boolean; // from Form render, don't use useFormState
  context: string; // from Form render, don't use useFormState
  isContextRequired: boolean;
  suggestedTagForNewContext: string;
  shouldSaveContext: boolean;
  selectedContext: UserEnquiryContext;
  setShouldSaveContext: Dispatch<SetStateAction<boolean>>;
  setSelectedContext: Dispatch<SetStateAction<UserEnquiryContext>>;
  nextStep?: () => void;
};

const ConfirmDetails: React.FC<ConfirmDetailsProps> = (props) => {
  const {
    invalid,
    context,
    isContextRequired,
    suggestedTagForNewContext,
    shouldSaveContext,
    selectedContext,
    setShouldSaveContext,
    setSelectedContext,
    nextStep,
  } = props;

  const { submit, change } = useForm();

  const { t } = useTranslation();

  const commonValidations = useValidations();

  const isCheckboxDisabled = useMemo(
    () => !context || context === selectedContext?.description,
    [context, selectedContext?.description],
  );

  const checkboxLabel = useMemo(() => {
    if (!context && !selectedContext) return t('saveContextToYourProfile');
    if (!selectedContext)
      return t('saveAsNewContext', { tag: suggestedTagForNewContext });
    return t('saveChangesTo', { tag: selectedContext.tag });
  }, [context, selectedContext, suggestedTagForNewContext, t]);

  const onEnquiryContextChange = useCallback(
    (context: UserEnquiryContext) =>
      setSelectedContext((prev) => {
        setShouldSaveContext(false);

        if (!prev || prev._id !== context._id) {
          change(CONTEXT_NAME, context.description);
          return context;
        }

        change(CONTEXT_NAME, undefined);
        return null;
      }),
    [change, setSelectedContext, setShouldSaveContext],
  );

  const onNextStepOrSubmit = useCallback(
    () => (nextStep ? nextStep() : submit()),
    [nextStep, submit],
  );

  const onConfirm = useCallback(() => {
    if (invalid) {
      submit();
      return;
    }

    onNextStepOrSubmit();
  }, [invalid, onNextStepOrSubmit, submit]);

  useEffect(() => {
    if (shouldSaveContext && isCheckboxDisabled) setShouldSaveContext(false);
  }, [isCheckboxDisabled, setShouldSaveContext, shouldSaveContext]);

  return (
    <div className="confirm-details">
      <p className="enquiry-title">{t('confirmYourDetails')}</p>
      <div className="fields-container">
        <Field
          label={t('firstName')}
          name="firstName"
          component={InputField}
          validate={commonValidations.name}
          required
        />
        <Field
          label={t('lastName')}
          name="lastName"
          component={InputField}
          validate={commonValidations.name}
          required
        />
        <EmailField required />
        <ContactNumberField required />
      </div>

      <EnquiryContextOptions
        isContextRequired={isContextRequired}
        selectedContextId={selectedContext?._id}
        onChange={onEnquiryContextChange}
      />

      <div>
        <Field
          name={CONTEXT_NAME}
          label={t('enquiryContextLabel')}
          placeholder={t('enquiryContextPlaceholder')}
          component={TextAreaField}
          maxLength={CONTEXT_MAX_LENGTH}
          rows={CONTEXT_ROWS}
          validate={validations.maxChars(CONTEXT_MAX_LENGTH)}
        />
      </div>

      <Checkbox
        label={checkboxLabel}
        disabled={isCheckboxDisabled}
        checked={shouldSaveContext}
        onChange={() => setShouldSaveContext((prev) => !prev)}
      />

      <div className="actions">
        <span />

        <Button onClick={onConfirm}>{t('confirmDetails')}</Button>
      </div>
    </div>
  );
};

export default ConfirmDetails;
